import { Component } from 'react';

import { renderColumn } from 'components/data/helpers';
import { type TEditableTableColumnDef } from 'horizon/types/TableColumnDef';
import { RenderWithAccess } from 'utils/usePermissions';

import EditActions from '../EditActions';

import { ActionsContainer, InlineEditableContainer, StyledSpinner } from './InlineEditable.style';

type OptionsType = {
  isRequired?: boolean;
  canEdit?: () => boolean;
  canRemove?: () => boolean;
  loading?: boolean;
  onSave?: (input: any) => Promise<void>;
  style?: any;
  shouldHideEditCallback?: (input: any) => boolean;
};

export function inlineEditableHeaderField(
  column: TEditableTableColumnDef,
  inputOptions: Pick<OptionsType, 'onSave' | 'shouldHideEditCallback'> = {
    shouldHideEditCallback: undefined,
  },
  isEditButtonVisible = true
): TEditableTableColumnDef {
  const options: OptionsType = {
    ...inputOptions,
    isRequired: false,
    canEdit: () => isEditButtonVisible,
    canRemove: () => true,
  };

  return {
    ...column,
    // Value and record really could be any types
    render: (value: any, record: any, index: number) => {
      return (
        <InlineEditable column={column} value={value} record={record} index={index} {...options} />
      );
    },
  };
}

// This component uses a combination of both rules and the isEditButtonVisible prop
// to determine whether to show the edit button.
// This is to continue to deprecate the use of rules going forward but need a way
// to dynamically show the button for ongoing work. - AN 9/27/23
export function inlineEditable(
  column: TEditableTableColumnDef,
  options: OptionsType = {},
  isEditButtonVisible = true
) {
  const { editRule } = column;
  if (!editRule) {
    return column;
  }

  options = {
    ...options,
    isRequired: options.isRequired,
    canEdit: () => isEditButtonVisible,
    canRemove: () => true,
  };

  return {
    ...column,
    render: (value: any, record: any, index: number) => {
      // If options are loading, always return the spinner
      if (options.loading) {
        return <StyledSpinner />;
      }
      // Sometimes we want to conditionally hide the ability to edit based on some check that is relevant to the record specifically
      // If that check callback is provided and evaluates to TRUE, render the normal column in a non-editable state
      if (options.shouldHideEditCallback && options.shouldHideEditCallback(record)) {
        return renderColumn(column, record, index);
      }
      // Otherwise, render the column within an InlineEditable
      return (
        <RenderWithAccess
          rules={[editRule]}
          fallbackRender={() => renderColumn(column, record, index)}
        >
          <InlineEditable
            column={column}
            value={value}
            record={record}
            index={index}
            {...options}
          />
        </RenderWithAccess>
      );
    },
  };
}

type EditableState = {
  editing: boolean;
  editedValue: any | null;
  isHovered: boolean;
  isInputValid: boolean;
};

type EditableProps = {
  overrideOnEdit?: (callback: Function) => any;
  onEdit?: Function;
  isRequired?: boolean;
  onSave?: Function;
  column?: TEditableTableColumnDef;
  value?: any;
  index?: number;
  record?: any;
  style?: any;
};
class InlineEditable extends Component<EditableProps, EditableState> {
  state = {
    editing: false,
    editedValue: null,
    isHovered: false,
    isInputValid: true,
  };

  isInputValid = (input: string | number | any) => {
    if (typeof input === 'string') {
      input = input.trim();
    }
    const isValid = input !== undefined && input !== null && input !== '';
    this.setState({
      isInputValid: isValid,
    });
  };

  handleMouseEnter = () => this.setState({ isHovered: true });
  handleMouseLeave = () => this.setState({ isHovered: false });
  handleEdit = () => {
    if (this.props.overrideOnEdit) {
      this.props.overrideOnEdit(() => {
        // let the caller decide if they want to call the default action
        this.setState({ editing: true });
      });
    } else {
      this.setState({ editing: true });
      if (this.props.onEdit) {
        this.props.onEdit();
      }
    }
  };
  handleCancel = () => this.setState({ editing: false, editedValue: null });
  handleChange = (editedValueContainer: any) => {
    this.setState({ editedValue: editedValueContainer });
    const editedValue = Object.values(editedValueContainer)[0];
    if (this.props.isRequired) {
      this.isInputValid(editedValue);
    }
  };

  handleSave = () => {
    this.props?.onSave && this.props.onSave(this.state.editedValue, this.props.column);
    this.setState({ editing: false, editedValue: null, isHovered: false });
  };

  render() {
    const { column, value, record, index, id, style, ...restProps }: any = this.props;
    const { editing, isHovered, isInputValid } = this.state;

    const actionsProps = {
      isInputValid,
      record,
      editing,
      ...restProps,
      onSave: this.handleSave,
      onEdit: this.handleEdit,
      onCancel: this.handleCancel,
    };

    return (
      <InlineEditableContainer
        data-testid={`inline-editable-${record.title}`}
        className="inline-editable"
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
        style={style}
      >
        {editing
          ? column.edit(record, { onChange: this.handleChange })
          : column.render(value, record, index)}
        <ActionsContainer id={id} visible={isHovered || editing}>
          <EditActions {...actionsProps} />
        </ActionsContainer>
      </InlineEditableContainer>
    );
  }
}
